import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
    Upload, FileSpreadsheet, AlertCircle, CheckCircle, XCircle, RefreshCw, Download, Pause, Play, Square,
    Trash2, Search, History, Sparkles, Copy, ExternalLink, ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
    ArrowUpDown, Edit3, Clock, Zap, Filter, Bell, ClipboardList, Eye, Monitor, X, RotateCcw, Database,
    TrendingUp, DollarSign, Package
} from 'lucide-react';
import * as XLSX from 'xlsx';
import authService from '../services/AuthService';
import { API_BASE } from '../consts';

// ═══════════════════════════════════════════════════════════════════════════
// AIP CSV Scan - scans a list of ASINs against the MIP server only
// (card-data-keepa + cached CC campaigns). No Amazon API calls.
// All scans + results are stored in IndexedDB so history survives reloads.
// ═══════════════════════════════════════════════════════════════════════════

const CONCURRENCY = 7;                 // same batch size the extension uses
const LIVE_REFRESH_MS = 1500;          // how often the live table refreshes while scanning
const META_SAVE_MS = 5000;             // how often scan progress is saved to IndexedDB
const DB_NAME = 'mip_aip_csv_scans';
const DB_VERSION = 1;
const ASIN_RE = /\bB[0-9A-Z]{9}\b/g;
const PAGE_SIZES = [25, 50, 100, 250];

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

// ── IndexedDB helpers ───────────────────────────────────────────────────────
let _dbPromise = null;
function openDb() {
    if (_dbPromise) return _dbPromise;
    _dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains('scans')) db.createObjectStore('scans', { keyPath: 'id' });
            if (!db.objectStoreNames.contains('results')) {
                const s = db.createObjectStore('results', { keyPath: ['scanId', 'asin'] });
                s.createIndex('scanId', 'scanId', { unique: false });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => { _dbPromise = null; reject(req.error); };
    });
    return _dbPromise;
}

function idbTx(storeName, mode, fn) {
    return openDb().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        let result;
        const r = fn(store);
        if (r) r.onsuccess = () => { result = r.result; };
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
    }));
}

const idb = {
    put: (store, val) => idbTx(store, 'readwrite', s => s.put(val)),
    getAll: (store) => idbTx(store, 'readonly', s => s.getAll()),
    delete: (store, key) => idbTx(store, 'readwrite', s => s.delete(key)),
    getByScan: (scanId) => idbTx('results', 'readonly', s => s.index('scanId').getAll(scanId)),
    deleteByScan: (scanId) => openDb().then(db => new Promise((resolve, reject) => {
        const tx = db.transaction('results', 'readwrite');
        const store = tx.objectStore('results');
        const req = store.index('scanId').openKeyCursor(IDBKeyRange.only(scanId));
        req.onsuccess = () => {
            const c = req.result;
            if (c) { store.delete(c.primaryKey); c.continue(); }
        };
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
    })),
};

// ── Server calls (MIP API only) ─────────────────────────────────────────────
async function fetchJson(url, opts = {}, tries = 3) {
    for (let attempt = 1; attempt <= tries; attempt++) {
        try {
            const r = await authService.authenticatedFetch(url, opts);
            if ((r.status === 429 || r.status >= 500) && attempt < tries) {
                await sleep(1500 * attempt);
                continue;
            }
            if (!r.ok) return { ok: false, status: r.status };
            const data = await r.json().catch(() => null);
            return { ok: true, data };
        } catch (e) {
            if (attempt < tries) { await sleep(1000 * attempt); continue; }
            return { ok: false, error: e?.message || 'network error' };
        }
    }
    return { ok: false, error: 'unknown' };
}

async function scanAsin(asin, scanId) {
    const [keepaRes, ccRes] = await Promise.all([
        fetchJson(`${API_BASE}/api/amazon/aip/card-data-keepa/${asin}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ campaign: null }),
        }),
        fetchJson(`${API_BASE}/api/amazon/aip/card-data/${asin}/cc`),
    ]);

    // A 404 on the CC cache just means "no cached campaigns" - not a failure
    const ccOk = ccRes.ok || ccRes.status === 404;

    if (!keepaRes.ok && !ccOk) {
        return {
            scanId, asin, status: 'failed', scannedAt: Date.now(),
            error: keepaRes.error || (keepaRes.status ? `HTTP ${keepaRes.status}` : 'request failed'),
        };
    }

    const quick = keepaRes.data?.quick || null;
    const rawInf = keepaRes.data?.inf;
    const inf = rawInf && rawInf !== 'err' && typeof rawInf === 'object' ? {
        upperVid: +(rawInf.upper_carousel?.avg_videos_per_fetch || 0).toFixed(1),
        upperInf: +(rawInf.upper_carousel?.avg_influencer_per_fetch || 0).toFixed(1),
        bottomVid: +(rawInf.bottom_carousel?.avg_videos_per_fetch || 0).toFixed(1),
        bottomInf: +(rawInf.bottom_carousel?.avg_influencer_per_fetch || 0).toFixed(1),
        totalInf: rawInf.total_inf_videos ?? null,
        isKeepa: rawInf.is_keepa === true,
    } : null;

    const campaigns = (ccRes.ok ? (ccRes.data?.cc?.campaigns || []) : []).map(c => ({
        campaignId: c.campaignId || '',
        campaignName: c.campaignName || '',
        brandName: c.brandName || '',
        commissionPercentage: c.commissionPercentage ?? 0,
        remainingBudget: c.remainingBudget ?? null,
        endMs: c.endMs || 0,
    }));

    return {
        scanId, asin, status: 'done', scannedAt: Date.now(),
        data: {
            title: quick?.title || '',
            brand: quick?.brand || '',
            image: quick?.image || '',
            category: quick?.category || '',
            bsr: quick?.sales_rank ?? null,
            price: quick?.price ?? null,
            monthlySold: quick?.monthly_sold ?? null,
            bsrDrops: quick?.sales_rank_drops_30 ?? null,
            variants: quick?.variants_count ?? null,
            inf,
            campaigns,
        },
    };
}

// ── Data helpers ────────────────────────────────────────────────────────────
function extractAsins(rows) {
    const seen = new Set();
    const ordered = [];
    let total = 0;
    rows.forEach(row => {
        (Array.isArray(row) ? row : [row]).forEach(cell => {
            if (cell === null || cell === undefined) return;
            const matches = String(cell).toUpperCase().match(ASIN_RE) || [];
            matches.forEach(a => {
                if (!/\d/.test(a)) return; // real ASINs contain a digit - skips words like "BACKGROUND"
                total++;
                if (!seen.has(a)) { seen.add(a); ordered.push(a); }
            });
        });
    });
    return { asins: ordered, totalFound: total, duplicates: total - ordered.length };
}

const isLiveCampaign = (c, now) =>
    (!c.endMs || c.endMs > now) && (c.remainingBudget === null || c.remainingBudget === undefined || c.remainingBudget > 0);

function estIncome(d, comm) {
    const price = d.price || 0;
    if (!price || !comm) return null;
    const sold = d.monthlySold > 0 ? d.monthlySold : 30;
    const inf = d.inf;
    const hasUpper = !!(inf?.upperVid > 0);
    const infCount = inf ? (inf.isKeepa ? Math.round((inf.upperInf || 0) + (inf.bottomInf || 0)) : (inf.totalInf || 0)) : 0;
    const watchRate = 0.015 * (hasUpper ? 2 : 1);
    return sold * watchRate * (1 / (infCount + 1)) * price * (comm / 100);
}

function summarize(recs) {
    const now = Date.now();
    let doneCount = 0, failedCount = 0, withCC = 0, campaignCount = 0, topComm = 0, commSum = 0;
    recs.forEach(r => {
        if (r.status === 'failed') { failedCount++; return; }
        doneCount++;
        const live = (r.data?.campaigns || []).filter(c => isLiveCampaign(c, now));
        if (live.length) {
            withCC++;
            campaignCount += live.length;
            live.forEach(c => {
                commSum += c.commissionPercentage || 0;
                topComm = Math.max(topComm, c.commissionPercentage || 0);
            });
        }
    });
    return { doneCount, failedCount, withCC, campaignCount, topComm, avgComm: campaignCount ? commSum / campaignCount : 0 };
}

function flattenRows(recs, { hideExpired }) {
    const now = Date.now();
    const rows = [];
    recs.forEach(r => {
        if (r.status !== 'done') return;
        const d = r.data || {};
        let camps = d.campaigns || [];
        if (hideExpired) camps = camps.filter(c => isLiveCampaign(c, now));
        const base = {
            asin: r.asin, title: d.title, image: d.image, category: d.category, bsr: d.bsr, price: d.price,
            monthlySold: d.monthlySold, bsrDrops: d.bsrDrops, variants: d.variants, inf: d.inf,
        };
        if (!camps.length) {
            rows.push({ ...base, key: `${r.asin}|none`, noCC: true, brand: d.brand || '', campaignName: '', campaignId: '', comm: null, budget: null, daysLeft: null, income: null });
        } else {
            camps.forEach((c, i) => {
                rows.push({
                    ...base,
                    key: `${r.asin}|${c.campaignId || i}`,
                    noCC: false,
                    brand: c.brandName || d.brand || '',
                    campaignName: c.campaignName,
                    campaignId: c.campaignId,
                    comm: c.commissionPercentage ?? 0,
                    budget: c.remainingBudget,
                    daysLeft: c.endMs ? Math.ceil((c.endMs - now) / 86400000) : null,
                    income: estIncome(d, c.commissionPercentage),
                });
            });
        }
    });
    return rows;
}

const fmtMoney = (v, digits = 0) => (v === null || v === undefined || isNaN(v)) ? '-' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
const fmtNum = (v) => (v === null || v === undefined || isNaN(v)) ? '-' : Number(v).toLocaleString();
const carouselStr = (vid, inf) => `${vid}=${+(vid - inf).toFixed(1)}+${inf}`;

const ccCampaignUrl = (id) => `https://affiliate-program.amazon.com/p/connect/request?adId=${encodeURIComponent(id)}&campaignId=${encodeURIComponent(id)}&recc=0&early-acc=0`;
const ccSearchUrl = (asin) => `https://affiliate-program.amazon.com/p/connect/requests?status=opportunity&type=affiliate-plus&sortBy=recommended_for_you&keyword=${asin}`;

function rowToExport(r) {
    return {
        ASIN: r.asin,
        Title: r.title || '',
        Brand: r.brand || '',
        Category: r.category || '',
        'Has CC': r.noCC ? 'No' : 'Yes',
        Campaign: r.campaignName || '',
        'Commission %': r.comm ?? '',
        'Days Left': r.daysLeft ?? '',
        'Upper (total=brand+inf)': r.inf ? carouselStr(r.inf.upperVid, r.inf.upperInf) : '',
        'Bottom (total=brand+inf)': r.inf ? carouselStr(r.inf.bottomVid, r.inf.bottomInf) : '',
        [r.inf?.isKeepa ? 'Avg Video Length (s)' : 'Total Influencer Videos']: r.inf?.totalInf ?? '',
        BSR: r.bsr ?? '',
        'Sales/Mo': r.monthlySold ?? '',
        Price: r.price ?? '',
        'Est. Mo. Income': r.income !== null && r.income !== undefined ? r.income.toFixed(2) : '',
        'Amazon URL': `https://www.amazon.com/dp/${r.asin}`,
        'Campaign URL': r.campaignId ? ccCampaignUrl(r.campaignId) : '',
    };
}

function exportRows(rows, baseName, format = 'xlsx') {
    const data = rows.map(rowToExport);
    const ws = XLSX.utils.json_to_sheet(data);
    const safe = (baseName || 'aip_scan').replace(/[^a-z0-9_-]+/gi, '_').slice(0, 60);
    if (format === 'csv') {
        const csv = XLSX.utils.sheet_to_csv(ws);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${safe}.csv`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(a.href), 2000);
    } else {
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Results');
        XLSX.writeFile(wb, `${safe}.xlsx`);
    }
}

function fmtDuration(sec) {
    if (!isFinite(sec) || sec < 0) return '-';
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = Math.floor(sec % 60);
    if (h) return `${h}h ${m}m`;
    if (m) return `${m}m ${s}s`;
    return `${s}s`;
}

// ═══════════════════════════════════════════════════════════════════════════
// Main page
// ═══════════════════════════════════════════════════════════════════════════
const AipCsvScanPage = ({ user }) => {
    const [activeTab, setActiveTab] = useState('new');
    const [scans, setScans] = useState([]);
    const [loadingScans, setLoadingScans] = useState(true);
    const [selectedScanId, setSelectedScanId] = useState(null);
    const [viewRecs, setViewRecs] = useState([]);
    const [loadingView, setLoadingView] = useState(false);
    const [runInfo, setRunInfo] = useState(null);
    const [toast, setToast] = useState(null);

    const engineRef = useRef({ running: false, paused: false, stopped: false, scanId: null });
    const liveMapRef = useRef(new Map());
    const wakeLockRef = useRef(null);
    const origTitleRef = useRef(typeof document !== 'undefined' ? document.title : '');

    const isRunning = !!runInfo;
    const selectedScan = scans.find(s => s.id === selectedScanId) || null;

    const showToast = useCallback((msg, type = 'success') => setToast({ msg, type, id: Date.now() }), []);
    useEffect(() => {
        if (!toast) return;
        const t = setTimeout(() => setToast(null), 3800);
        return () => clearTimeout(t);
    }, [toast]);

    const refreshScans = useCallback(async () => {
        try {
            const all = await idb.getAll('scans');
            all.sort((a, b) => b.createdAt - a.createdAt);
            setScans(all);
        } catch (e) {
            console.error('Failed to load scans', e);
        } finally {
            setLoadingScans(false);
        }
    }, []);

    // On load: any scan still marked "running" was cut off when the page closed
    useEffect(() => {
        (async () => {
            try {
                const all = await idb.getAll('scans');
                for (const s of all) {
                    if (s.status === 'running') await idb.put('scans', { ...s, status: 'interrupted' });
                }
            } catch { /* ignore */ }
            refreshScans();
        })();
    }, [refreshScans]);

    // Warn before leaving while a scan runs
    useEffect(() => {
        if (!isRunning) return;
        const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isRunning]);

    // Progress in the browser tab title
    useEffect(() => {
        if (!runInfo) { document.title = origTitleRef.current; return; }
        const pct = runInfo.total ? Math.floor((runInfo.processed / runInfo.total) * 100) : 0;
        document.title = `${runInfo.paused ? '⏸' : '⏳'} ${pct}% · AIP CSV Scan`;
    }, [runInfo]);
    useEffect(() => () => { document.title = origTitleRef.current; }, []);

    // Keep the screen awake while scanning (where supported)
    const requestWakeLock = useCallback(async () => {
        try {
            if ('wakeLock' in navigator && !wakeLockRef.current) {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
                wakeLockRef.current.addEventListener?.('release', () => { wakeLockRef.current = null; });
            }
        } catch { /* not supported / denied */ }
    }, []);
    const releaseWakeLock = useCallback(() => {
        try { wakeLockRef.current?.release(); } catch { /* ignore */ }
        wakeLockRef.current = null;
    }, []);
    useEffect(() => {
        const onVis = () => { if (document.visibilityState === 'visible' && engineRef.current.running) requestWakeLock(); };
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, [requestWakeLock]);

    // ── Scan engine ─────────────────────────────────────────────────────────
    const startScan = useCallback(async (meta, todo, existingMap) => {
        if (engineRef.current.running) {
            showToast('Another scan is already running. Pause or stop it first.', 'error');
            return;
        }
        if (!todo.length) {
            showToast('Nothing left to scan in this list.', 'info');
            return;
        }

        const eng = { running: true, paused: false, stopped: false, scanId: meta.id, doneThisRun: 0, failedThisRun: 0 };
        engineRef.current = eng;
        liveMapRef.current = existingMap || new Map();

        let current = { ...meta, status: 'running', updatedAt: Date.now() };
        await idb.put('scans', current);
        await refreshScans();
        setSelectedScanId(meta.id);
        setActiveTab('history');

        const startedAt = Date.now();
        const snapshot = () => setRunInfo({
            scanId: meta.id,
            paused: eng.paused,
            startedAt,
            total: meta.asins.length,
            processed: liveMapRef.current.size,
            todo: todo.length,
            doneThisRun: eng.doneThisRun,
            failedThisRun: eng.failedThisRun,
            tick: Date.now(),
        });
        snapshot();
        requestWakeLock();

        let lastMetaSave = 0;
        const ticker = setInterval(() => {
            snapshot();
            if (Date.now() - lastMetaSave > META_SAVE_MS) {
                lastMetaSave = Date.now();
                current = {
                    ...current,
                    ...summarize([...liveMapRef.current.values()]),
                    status: eng.paused ? 'paused' : 'running',
                    updatedAt: Date.now(),
                };
                idb.put('scans', current).then(refreshScans).catch(() => { });
            }
        }, LIVE_REFRESH_MS);

        let idx = 0;
        const worker = async () => {
            while (true) {
                while (eng.paused && !eng.stopped) await sleep(300);
                if (eng.stopped) return;
                const i = idx++;
                if (i >= todo.length) return;
                const rec = await scanAsin(todo[i], meta.id);
                liveMapRef.current.set(rec.asin, rec);
                if (rec.status === 'failed') eng.failedThisRun++; else eng.doneThisRun++;
                try { await idb.put('results', rec); } catch (e) { console.warn('IDB write failed', e); }
            }
        };

        await Promise.all(Array.from({ length: Math.min(CONCURRENCY, todo.length) }, worker));
        clearInterval(ticker);

        const recs = [...liveMapRef.current.values()];
        const allDone = meta.asins.every(a => liveMapRef.current.has(a));
        const finalStatus = !eng.stopped && allDone ? 'completed' : 'stopped';
        current = { ...current, ...summarize(recs), status: finalStatus, updatedAt: Date.now(), finishedAt: Date.now() };
        await idb.put('scans', current);

        eng.running = false;
        setRunInfo(null);
        releaseWakeLock();
        await refreshScans();

        if (finalStatus === 'completed') {
            showToast(`✅ "${meta.name}" finished - ${current.withCC} of ${current.doneCount} ASINs have CC campaigns.`);
            try {
                if ('Notification' in window && Notification.permission === 'granted') {
                    new Notification('AIP CSV Scan finished', { body: `${meta.name}: ${current.withCC} ASINs with CC campaigns.` });
                }
            } catch { /* ignore */ }
        } else {
            showToast(`Scan stopped. You can resume "${meta.name}" any time from My Scans.`, 'info');
        }
    }, [refreshScans, requestWakeLock, releaseWakeLock, showToast]);

    const createAndStartScan = useCallback(async ({ name, fileName, asins }) => {
        const meta = {
            id: `scan_${Date.now()}`,
            name: name.trim(),
            fileName: fileName || '',
            createdAt: Date.now(),
            updatedAt: Date.now(),
            asins,
            total: asins.length,
            doneCount: 0, failedCount: 0, withCC: 0, campaignCount: 0, topComm: 0, avgComm: 0,
            status: 'pending',
        };
        await idb.put('scans', meta);
        startScan(meta, asins, new Map());
    }, [startScan]);

    const pauseScan = useCallback(async () => {
        const eng = engineRef.current;
        if (!eng.running) return;
        eng.paused = true;
        setRunInfo(r => r ? { ...r, paused: true } : r);
        const s = scans.find(x => x.id === eng.scanId);
        if (s) { await idb.put('scans', { ...s, ...summarize([...liveMapRef.current.values()]), status: 'paused', updatedAt: Date.now() }); refreshScans(); }
    }, [scans, refreshScans]);

    const unpauseScan = useCallback(async () => {
        const eng = engineRef.current;
        if (!eng.running) return;
        eng.paused = false;
        setRunInfo(r => r ? { ...r, paused: false } : r);
        requestWakeLock();
        const s = scans.find(x => x.id === eng.scanId);
        if (s) { await idb.put('scans', { ...s, status: 'running', updatedAt: Date.now() }); refreshScans(); }
    }, [scans, refreshScans, requestWakeLock]);

    const stopScan = useCallback(() => {
        const eng = engineRef.current;
        if (!eng.running) return;
        if (!window.confirm('Stop this scan? Everything scanned so far is saved and you can resume later.')) return;
        eng.stopped = true;
        eng.paused = false;
    }, []);

    const resumeScan = useCallback(async (meta) => {
        const eng = engineRef.current;
        if (eng.running && eng.scanId === meta.id) { if (eng.paused) unpauseScan(); return; }
        const recs = await idb.getByScan(meta.id);
        const map = new Map(recs.map(r => [r.asin, r]));
        const todo = meta.asins.filter(a => !map.has(a));
        startScan(meta, todo, map);
    }, [startScan, unpauseScan]);

    const retryFailed = useCallback(async (meta) => {
        const recs = await idb.getByScan(meta.id);
        const map = new Map(recs.map(r => [r.asin, r]));
        const todo = recs.filter(r => r.status === 'failed').map(r => r.asin);
        if (!todo.length) { showToast('No failed ASINs to retry.', 'info'); return; }
        startScan(meta, todo, map);
    }, [startScan, showToast]);

    const rescanAll = useCallback(async (meta) => {
        if (!window.confirm(`Rescan all ${meta.asins.length} ASINs in "${meta.name}"? Old results for this scan will be replaced.`)) return;
        await idb.deleteByScan(meta.id);
        startScan({ ...meta, doneCount: 0, failedCount: 0, withCC: 0, campaignCount: 0 }, meta.asins, new Map());
    }, [startScan]);

    const deleteScan = useCallback(async (meta) => {
        if (engineRef.current.running && engineRef.current.scanId === meta.id) {
            showToast('Stop this scan before deleting it.', 'error');
            return;
        }
        if (!window.confirm(`Delete "${meta.name}" and all its results? This can't be undone.`)) return;
        await idb.deleteByScan(meta.id);
        await idb.delete('scans', meta.id);
        if (selectedScanId === meta.id) setSelectedScanId(null);
        refreshScans();
        showToast('Scan deleted.');
    }, [selectedScanId, refreshScans, showToast]);

    const renameScan = useCallback(async (meta, newName) => {
        const name = (newName || '').trim();
        if (!name || name === meta.name) return;
        await idb.put('scans', { ...meta, name, updatedAt: Date.now() });
        refreshScans();
    }, [refreshScans]);

    const exportScan = useCallback(async (meta, format) => {
        const recs = (engineRef.current.running && engineRef.current.scanId === meta.id)
            ? [...liveMapRef.current.values()]
            : await idb.getByScan(meta.id);
        const rows = flattenRows(recs, { hideExpired: true });
        if (!rows.length) { showToast('No results to export yet.', 'info'); return; }
        exportRows(rows, meta.name, format);
    }, [showToast]);

    // ── Results for the scan being viewed ────────────────────────────────────
    const liveTick = runInfo && runInfo.scanId === selectedScanId ? runInfo.tick : null;
    useEffect(() => {
        if (!selectedScanId) { setViewRecs([]); return; }
        if (engineRef.current.running && engineRef.current.scanId === selectedScanId) {
            setViewRecs([...liveMapRef.current.values()]);
            return;
        }
        let cancelled = false;
        setLoadingView(true);
        idb.getByScan(selectedScanId)
            .then(r => { if (!cancelled) setViewRecs(r || []); })
            .catch(() => { if (!cancelled) setViewRecs([]); })
            .finally(() => { if (!cancelled) setLoadingView(false); });
        return () => { cancelled = true; };
    }, [selectedScanId, liveTick, isRunning]);

    const openScan = (id) => {
        setSelectedScanId(id);
        setTimeout(() => document.getElementById('aip-scan-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4">
            {/* Animated background */}
            <div className="fixed inset-0 opacity-10 pointer-events-none">
                <div className="absolute top-0 -left-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
                <div className="absolute bottom-0 -right-4 w-96 h-96 rounded-full filter blur-3xl"
                    style={{ background: 'linear-gradient(90deg, rgb(20, 184, 166) 0%, rgb(37, 99, 235) 100%)' }}></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <Sparkles className="text-teal-600" size={40} />
                        AIP CSV Scan
                    </h1>
                    <p className="text-gray-600">
                        Upload a list of ASINs and instantly see which ones have Creator Connections campaigns, their commission,
                        video competition, BSR, sales and estimated income.
                    </p>
                </div>

                {/* Running banner (visible everywhere on the page) */}
                {isRunning && (
                    <RunningBanner
                        runInfo={runInfo}
                        scanName={scans.find(s => s.id === runInfo.scanId)?.name || 'Scan'}
                        onPause={pauseScan}
                        onResume={unpauseScan}
                        onStop={stopScan}
                        onView={() => openScan(runInfo.scanId)}
                    />
                )}

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-lg mb-6">
                    <div className="flex border-b">
                        <button
                            onClick={() => setActiveTab('new')}
                            className={`flex-1 py-4 px-6 font-semibold transition flex items-center justify-center gap-2 ${activeTab === 'new'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            <Upload size={20} />
                            New Scan
                        </button>
                        <button
                            onClick={() => { setActiveTab('history'); refreshScans(); }}
                            className={`flex-1 py-4 px-6 font-semibold transition flex items-center justify-center gap-2 ${activeTab === 'history'
                                ? 'text-teal-600 border-b-2 border-teal-600'
                                : 'text-gray-600 hover:text-gray-800'}`}
                        >
                            <History size={20} />
                            My Scans ({loadingScans ? <RefreshCw className="animate-spin" size={16} /> : scans.length})
                        </button>
                    </div>

                    <div className="p-6">
                        {activeTab === 'new' ? (
                            <NewScanTab
                                isRunning={isRunning}
                                existingNames={scans.map(s => s.name.toLowerCase())}
                                onStart={createAndStartScan}
                                showToast={showToast}
                            />
                        ) : (
                            <HistoryTab
                                scans={scans}
                                loading={loadingScans}
                                runInfo={runInfo}
                                selectedScanId={selectedScanId}
                                onOpen={openScan}
                                onResume={resumeScan}
                                onPause={pauseScan}
                                onUnpause={unpauseScan}
                                onRetryFailed={retryFailed}
                                onRescan={rescanAll}
                                onDelete={deleteScan}
                                onRename={renameScan}
                                onExport={exportScan}
                                onGoNew={() => setActiveTab('new')}
                            />
                        )}
                    </div>
                </div>

                {/* Selected scan results */}
                {selectedScan && (
                    <div id="aip-scan-results">
                        <ScanView
                            scan={selectedScan}
                            recs={viewRecs}
                            loading={loadingView}
                            runInfo={runInfo && runInfo.scanId === selectedScan.id ? runInfo : null}
                            onClose={() => setSelectedScanId(null)}
                            onPause={pauseScan}
                            onUnpause={unpauseScan}
                            onStop={stopScan}
                            onResume={() => resumeScan(selectedScan)}
                            onRetryFailed={() => retryFailed(selectedScan)}
                            onRename={(n) => renameScan(selectedScan, n)}
                            anotherRunning={isRunning && runInfo.scanId !== selectedScan.id}
                            showToast={showToast}
                        />
                    </div>
                )}
            </div>

            {/* Toast */}
            {toast && (
                <div key={toast.id} className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-6 py-3 rounded-full shadow-xl flex items-center gap-3 text-white ${toast.type === 'error' ? 'bg-red-600' : toast.type === 'info' ? 'bg-gray-900' : 'bg-teal-600'}`}>
                    <span className="text-sm font-semibold">{toast.msg}</span>
                    <button onClick={() => setToast(null)} className="text-white/70 hover:text-white"><X size={16} /></button>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// Running banner
// ═══════════════════════════════════════════════════════════════════════════
const RunningBanner = ({ runInfo, scanName, onPause, onResume, onStop, onView }) => {
    const pct = runInfo.total ? Math.floor((runInfo.processed / runInfo.total) * 100) : 0;
    const handled = runInfo.doneThisRun + runInfo.failedThisRun;
    const elapsed = (Date.now() - runInfo.startedAt) / 1000;
    const rate = elapsed > 0 ? handled / (elapsed / 60) : 0;
    const remaining = runInfo.todo - handled;
    const eta = rate > 0 ? (remaining / rate) * 60 : Infinity;

    return (
        <div className={`mb-6 rounded-xl shadow-lg border-2 p-4 ${runInfo.paused ? 'bg-orange-50 border-orange-300' : 'bg-white border-teal-300'}`}>
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                    {runInfo.paused
                        ? <Pause className="text-orange-600 flex-shrink-0" size={28} />
                        : <RefreshCw className="text-teal-600 animate-spin flex-shrink-0" size={28} />}
                    <div className="min-w-0">
                        <div className="font-bold text-gray-800 truncate">
                            {runInfo.paused ? 'Paused' : 'Scanning'} “{scanName}” - {pct}%
                        </div>
                        <div className="text-xs text-gray-600 flex flex-wrap gap-x-4">
                            <span>{fmtNum(runInfo.processed)} / {fmtNum(runInfo.total)} ASINs</span>
                            {!runInfo.paused && <span><Zap className="inline" size={12} /> {rate.toFixed(1)}/min</span>}
                            {!runInfo.paused && <span><Clock className="inline" size={12} /> ~{fmtDuration(eta)} left</span>}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={onView} className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold flex items-center gap-1"><Eye size={16} /> View</button>
                    {runInfo.paused
                        ? <button onClick={onResume} className="px-3 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold flex items-center gap-1"><Play size={16} /> Resume</button>
                        : <button onClick={onPause} className="px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-1"><Pause size={16} /> Pause</button>}
                    <button onClick={onStop} className="px-3 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold flex items-center gap-1"><Square size={16} /> Stop</button>
                </div>
            </div>
            <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${runInfo.paused ? 'bg-orange-400' : 'bg-gradient-to-r from-teal-500 to-blue-600'}`} style={{ width: `${pct}%` }} />
            </div>
            <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                <Monitor size={12} /> Keep this tab open until the scan finishes. If you close it, everything scanned so far is saved and you can resume from My Scans.
            </div>
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// New scan tab
// ═══════════════════════════════════════════════════════════════════════════
const NewScanTab = ({ isRunning, existingNames, onStart, showToast }) => {
    const [step, setStep] = useState(1);
    const [mode, setMode] = useState('file');
    const [file, setFile] = useState(null);
    const [pasteText, setPasteText] = useState('');
    const [parsed, setParsed] = useState(null);
    const [scanName, setScanName] = useState('');
    const [notify, setNotify] = useState(typeof Notification !== 'undefined' && Notification.permission === 'granted');
    const fileInputRef = useRef(null);

    const reset = () => {
        setStep(1); setFile(null); setPasteText(''); setParsed(null); setScanName('');
    };

    const handleFile = (f) => {
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['csv', 'xlsx', 'xls', 'txt'].includes(ext)) {
            showToast('Please upload a CSV, Excel or TXT file.', 'error');
            return;
        }
        setFile(f);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                let rows;
                if (ext === 'txt') {
                    rows = String(e.target.result || '').split(/\r?\n/).map(l => [l]);
                } else {
                    const wb = XLSX.read(e.target.result, { type: 'array' });
                    rows = [];
                    wb.SheetNames.forEach(n => {
                        rows.push(...XLSX.utils.sheet_to_json(wb.Sheets[n], { header: 1, raw: false }));
                    });
                }
                const result = extractAsins(rows);
                if (!result.asins.length) {
                    showToast('No ASINs found in that file. ASINs look like B0XXXXXXXX.', 'error');
                    return;
                }
                setParsed({ ...result, rowsScanned: rows.length, source: f.name });
                setScanName(`${f.name.replace(/\.[^.]+$/, '')} - ${new Date().toLocaleDateString()}`);
                setStep(2);
            } catch (err) {
                console.error(err);
                showToast('Could not read that file. Make sure it is a valid CSV or Excel file.', 'error');
            }
        };
        if (ext === 'txt') reader.readAsText(f); else reader.readAsArrayBuffer(f);
    };

    const handlePaste = () => {
        const rows = pasteText.split(/\r?\n/).map(l => [l]);
        const result = extractAsins(rows);
        if (!result.asins.length) {
            showToast('No ASINs found. Paste ASINs or Amazon product links, one per line.', 'error');
            return;
        }
        setFile(null);
        setParsed({ ...result, rowsScanned: rows.length, source: 'Pasted list' });
        setScanName(`Pasted ASINs - ${new Date().toLocaleString()}`);
        setStep(2);
    };

    const downloadTemplate = () => {
        const ws = XLSX.utils.aoa_to_sheet([
            ['ASIN', 'Notes (optional)'],
            ['B08N5WRWNW', 'Example'],
            ['B07FZ8S74R', ''],
            ['https://www.amazon.com/dp/B09B8V1LZ3', 'Product links work too'],
        ]);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'ASINs');
        XLSX.writeFile(wb, 'aip_csv_scan_template.xlsx');
    };

    const askNotify = async () => {
        try {
            if (!('Notification' in window)) { showToast('Your browser does not support notifications.', 'info'); return; }
            const p = await Notification.requestPermission();
            setNotify(p === 'granted');
            if (p === 'granted') showToast('We’ll notify you when the scan finishes.');
        } catch { /* ignore */ }
    };

    const start = () => {
        if (!scanName.trim()) { showToast('Please give this scan a name.', 'error'); return; }
        if (existingNames.includes(scanName.trim().toLowerCase())) { showToast('A scan with this name already exists.', 'error'); return; }
        if (isRunning) { showToast('A scan is already running. Pause or stop it first.', 'error'); return; }
        onStart({ name: scanName, fileName: parsed.source, asins: parsed.asins });
        reset();
    };

    return (
        <div>
            {/* Progress Steps */}
            <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <div className="flex items-center justify-between">
                    {[
                        { num: 1, label: 'Add ASINs', icon: Upload },
                        { num: 2, label: 'Review & Start', icon: CheckCircle },
                    ].map((s, idx) => (
                        <React.Fragment key={s.num}>
                            <div className="flex flex-col items-center">
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold transition ${step >= s.num ? 'bg-teal-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                                    {step > s.num ? <CheckCircle size={24} /> : <s.icon size={24} />}
                                </div>
                                <span className={`mt-2 text-sm font-semibold ${step >= s.num ? 'text-teal-600' : 'text-gray-500'}`}>{s.label}</span>
                            </div>
                            {idx < 1 && <div className={`flex-1 h-1 mx-4 transition ${step > s.num ? 'bg-teal-600' : 'bg-gray-200'}`}></div>}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {step === 1 && (
                <div className="space-y-6">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Add Your ASINs</h2>
                        <p className="text-gray-600">Upload a file or paste a list. We find every ASIN automatically - any column, any layout, product links included.</p>
                    </div>

                    <div className="flex justify-center">
                        <div className="inline-flex bg-gray-100 rounded-lg p-1">
                            <button onClick={() => setMode('file')} className={`px-5 py-2 rounded-md text-sm font-semibold transition flex items-center gap-2 ${mode === 'file' ? 'bg-white shadow text-teal-700' : 'text-gray-600'}`}>
                                <FileSpreadsheet size={16} /> Upload File
                            </button>
                            <button onClick={() => setMode('paste')} className={`px-5 py-2 rounded-md text-sm font-semibold transition flex items-center gap-2 ${mode === 'paste' ? 'bg-white shadow text-teal-700' : 'text-gray-600'}`}>
                                <ClipboardList size={16} /> Paste ASINs
                            </button>
                        </div>
                    </div>

                    {mode === 'file' ? (
                        <div
                            className="border-4 border-dashed border-teal-300 rounded-xl p-12 text-center hover:border-teal-500 transition cursor-pointer bg-teal-50"
                            onClick={() => fileInputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-teal-500', 'bg-teal-100'); }}
                            onDragEnter={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-teal-500', 'bg-teal-100'); }}
                            onDragLeave={(e) => { e.currentTarget.classList.remove('border-teal-500', 'bg-teal-100'); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                e.currentTarget.classList.remove('border-teal-500', 'bg-teal-100');
                                handleFile(e.dataTransfer.files[0]);
                            }}
                        >
                            <Upload className="mx-auto text-teal-600 mb-4" size={64} />
                            <p className="text-xl font-semibold text-gray-800 mb-2">Click to upload or drag and drop</p>
                            <p className="text-gray-600">CSV, XLSX, XLS or TXT</p>
                            {file && (
                                <div className="mt-4 inline-flex items-center gap-2 bg-teal-100 px-4 py-2 rounded-lg">
                                    <FileSpreadsheet className="text-teal-600" size={20} />
                                    <span className="text-teal-800 font-semibold">{file.name}</span>
                                </div>
                            )}
                            <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden"
                                onChange={(e) => { handleFile(e.target.files[0]); e.target.value = ''; }} />
                        </div>
                    ) : (
                        <div className="space-y-3">
                            <textarea
                                value={pasteText}
                                onChange={(e) => setPasteText(e.target.value)}
                                rows={10}
                                placeholder={'B08N5WRWNW\nB07FZ8S74R\nhttps://www.amazon.com/dp/B09B8V1LZ3'}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono text-sm"
                            />
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">{extractAsins(pasteText.split(/\r?\n/).map(l => [l])).asins.length} unique ASINs detected</span>
                                <button onClick={handlePaste} disabled={!pasteText.trim()}
                                    className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed">
                                    Continue
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="flex justify-center">
                        <button onClick={downloadTemplate} className="flex items-center gap-2 text-teal-600 hover:text-teal-800 font-semibold">
                            <Download size={20} /> Download Template File
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                                <div className="text-sm text-blue-800">
                                    <p className="font-semibold mb-2">What you get for every ASIN:</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>All Creator Connections campaigns (commission, days left)</li>
                                        <li>Upper & bottom carousel video competition</li>
                                        <li>BSR, monthly sales, price and an estimated monthly income</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <Monitor className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                                <div className="text-sm text-amber-800">
                                    <p className="font-semibold mb-2">Keep this page open while scanning</p>
                                    <p>The scan runs in your browser. If you close the tab, nothing is lost - results are saved on this device and you can resume from <b>My Scans</b>.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {step === 2 && parsed && (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Review & Start</h2>
                        <p className="text-gray-600">Source: <b>{parsed.source}</b></p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-center gap-3">
                            <CheckCircle className="text-green-600" size={32} />
                            <div>
                                <p className="text-sm text-green-700 font-semibold">Unique ASINs</p>
                                <p className="text-2xl font-bold text-green-800">{fmtNum(parsed.asins.length)}</p>
                            </div>
                        </div>
                        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 flex items-center gap-3">
                            <Copy className="text-yellow-600" size={32} />
                            <div>
                                <p className="text-sm text-yellow-700 font-semibold">Duplicates Removed</p>
                                <p className="text-2xl font-bold text-yellow-800">{fmtNum(parsed.duplicates)}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Preview (first {Math.min(30, parsed.asins.length)})</p>
                        <div className="flex flex-wrap gap-2">
                            {parsed.asins.slice(0, 30).map(a => (
                                <span key={a} className="font-mono text-xs bg-white border border-gray-200 px-2 py-1 rounded">{a}</span>
                            ))}
                            {parsed.asins.length > 30 && <span className="text-xs text-gray-500 self-center">+{fmtNum(parsed.asins.length - 30)} more</span>}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Scan Name <span className="text-red-500">*</span></label>
                        <input type="text" value={scanName} onChange={(e) => setScanName(e.target.value)}
                            className="w-full md:w-1/2 px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                        <p className="mt-2 text-sm text-gray-600">You’ll find it later under My Scans.</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <button onClick={askNotify} disabled={notify}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 border-2 transition ${notify ? 'border-green-200 bg-green-50 text-green-700' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                            <Bell size={16} /> {notify ? 'Notifications on' : 'Notify me when done'}
                        </button>
                        {isRunning && (
                            <span className="text-sm text-orange-700 bg-orange-50 border border-orange-200 px-3 py-2 rounded-lg flex items-center gap-2">
                                <AlertCircle size={16} /> Another scan is running - pause or stop it to start a new one.
                            </span>
                        )}
                    </div>

                    <div className="flex gap-4 pt-2">
                        <button onClick={reset} className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold">Back</button>
                        <button onClick={start} disabled={!scanName.trim() || isRunning}
                            className="flex-1 bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                            <Zap size={20} /> Start Scanning {fmtNum(parsed.asins.length)} ASINs
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// History tab
// ═══════════════════════════════════════════════════════════════════════════
const statusBadge = (status) => {
    const map = {
        completed: ['text-green-700 bg-green-100', <CheckCircle size={16} />, 'Completed'],
        running: ['text-blue-700 bg-blue-100', <RefreshCw className="animate-spin" size={16} />, 'Scanning'],
        pending: ['text-yellow-700 bg-yellow-100', <AlertCircle size={16} />, 'Pending'],
        paused: ['text-orange-700 bg-orange-100', <Pause size={16} />, 'Paused'],
        stopped: ['text-gray-700 bg-gray-100', <Square size={16} />, 'Stopped'],
        interrupted: ['text-red-700 bg-red-100', <AlertCircle size={16} />, 'Interrupted - resume to continue'],
    };
    const [cls, icon, label] = map[status] || map.pending;
    return <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${cls}`}>{icon} {label}</span>;
};

const HistoryTab = ({ scans, loading, runInfo, selectedScanId, onOpen, onResume, onPause, onUnpause, onRetryFailed, onRescan, onDelete, onRename, onExport, onGoNew }) => {
    const [query, setQuery] = useState('');
    const [storage, setStorage] = useState(null);

    useEffect(() => {
        navigator.storage?.estimate?.().then(est => setStorage(est)).catch(() => { });
    }, [scans.length]);

    if (loading) {
        return (
            <div className="text-center py-12">
                <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={64} />
                <p className="text-gray-600 text-lg font-semibold">Loading your scans...</p>
            </div>
        );
    }

    if (!scans.length) {
        return (
            <div className="text-center py-12">
                <FileSpreadsheet className="mx-auto text-gray-400 mb-4" size={64} />
                <p className="text-gray-600 text-lg">No scans yet</p>
                <p className="text-gray-500 text-sm mb-4">Upload your first ASIN list to get started!</p>
                <button onClick={onGoNew} className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition">Start a Scan</button>
            </div>
        );
    }

    const filtered = scans.filter(s => !query || s.name.toLowerCase().includes(query.toLowerCase()) || (s.fileName || '').toLowerCase().includes(query.toLowerCase()));
    const order = { running: 0, paused: 1, interrupted: 2, pending: 3, stopped: 4, completed: 5 };
    const sorted = [...filtered].sort((a, b) => {
        const liveA = runInfo?.scanId === a.id ? -1 : (order[a.status] ?? 6);
        const liveB = runInfo?.scanId === b.id ? -1 : (order[b.status] ?? 6);
        return liveA - liveB || b.createdAt - a.createdAt;
    });

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search scans..."
                        className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                </div>
                {storage?.usage !== undefined && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                        <Database size={14} /> Saved on this device · {(storage.usage / 1048576).toFixed(1)} MB used
                    </span>
                )}
            </div>

            {sorted.map(scan => {
                const isLive = runInfo?.scanId === scan.id;
                const processed = isLive ? runInfo.processed : (scan.doneCount || 0) + (scan.failedCount || 0);
                const pct = scan.total ? Math.floor((processed / scan.total) * 100) : 0;
                const status = isLive ? (runInfo.paused ? 'paused' : 'running') : scan.status;
                const incomplete = processed < scan.total;

                return (
                    <div key={scan.id} className={`bg-white border rounded-lg hover:shadow-lg transition p-6 ${selectedScanId === scan.id ? 'border-teal-400 ring-2 ring-teal-100' : 'border-gray-200'}`}>
                        <div className="flex items-start justify-between mb-4 gap-4">
                            <div className="flex-1 min-w-0">
                                <h3 className="text-xl font-bold text-gray-800 mb-2 truncate">
                                    {scan.name}
                                    <span className="text-sm text-gray-500 font-normal ml-2">{new Date(scan.createdAt).toLocaleString()}</span>
                                </h3>
                                <div className="flex flex-wrap items-center gap-2">
                                    {statusBadge(status)}
                                    {scan.fileName && <span className="text-sm text-gray-600">📄 {scan.fileName}</span>}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                {isLive && !runInfo.paused && (
                                    <button onClick={onPause} title="Pause" className="text-orange-600 hover:text-orange-800 p-2 hover:bg-orange-50 rounded-lg transition"><Pause size={20} /></button>
                                )}
                                {isLive && runInfo.paused && (
                                    <button onClick={onUnpause} title="Resume" className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition"><Play size={20} /></button>
                                )}
                                {!isLive && incomplete && (
                                    <button onClick={() => onResume(scan)} disabled={!!runInfo} title={runInfo ? 'Another scan is running' : 'Resume where it stopped'}
                                        className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded-lg transition disabled:opacity-40"><Play size={20} /></button>
                                )}
                                <button onClick={() => { const n = window.prompt('Rename scan', scan.name); if (n !== null) onRename(scan, n); }} title="Rename"
                                    className="text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg transition"><Edit3 size={20} /></button>
                                <button onClick={() => onExport(scan, 'xlsx')} title="Export to Excel"
                                    className="text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg transition"><Download size={20} /></button>
                                {!isLive && (
                                    <button onClick={() => onRescan(scan)} disabled={!!runInfo} title="Rescan everything"
                                        className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg font-semibold transition disabled:opacity-40 flex items-center gap-1 text-sm">
                                        <RotateCcw size={16} /> Rescan
                                    </button>
                                )}
                                <button onClick={() => onDelete(scan)} disabled={isLive} title="Delete"
                                    className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition disabled:opacity-40"><Trash2 size={20} /></button>
                            </div>
                        </div>

                        <div className="mb-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>{fmtNum(processed)} / {fmtNum(scan.total)} scanned</span>
                                <span>{pct}%</span>
                            </div>
                            <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-4">
                                <div className="h-full bg-gradient-to-r from-teal-500 to-blue-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                <MiniStat label="ASINs" value={fmtNum(scan.total)} cls="bg-blue-50 text-blue-800" />
                                <MiniStat label="With CC" value={fmtNum(scan.withCC || 0)} cls="bg-green-50 text-green-800" />
                                <MiniStat label="Campaigns" value={fmtNum(scan.campaignCount || 0)} cls="bg-teal-50 text-teal-800" />
                                <MiniStat label="Top Commission" value={scan.topComm ? `${scan.topComm}%` : '-'} cls="bg-purple-100 text-purple-800 border border-purple-300" />
                                <MiniStat label="Failed" value={fmtNum(scan.failedCount || 0)} cls="bg-red-50 text-red-800"
                                    action={!isLive && scan.failedCount > 0 && !runInfo ? () => onRetryFailed(scan) : null} />
                            </div>
                        </div>

                        <button onClick={() => onOpen(scan.id)}
                            className="w-full bg-gradient-to-r from-teal-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center justify-center gap-2">
                            <Eye size={20} /> View Results
                        </button>
                    </div>
                );
            })}

            <p className="text-xs text-gray-400 text-center">
                Scans are stored in this browser only. Clearing site data or using another device/browser won’t show them.
            </p>
        </div>
    );
};

const MiniStat = ({ label, value, cls, action }) => (
    <div className={`text-center p-2 rounded ${cls}`}>
        <div className="text-xs font-semibold opacity-80">{label}</div>
        <div className="text-lg font-bold">{value}</div>
        {action && <button onClick={action} className="text-[11px] font-semibold underline mt-0.5">Retry failed</button>}
    </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Scan view (header + stats + table)
// ═══════════════════════════════════════════════════════════════════════════
const ScanView = ({ scan, recs, loading, runInfo, onClose, onPause, onUnpause, onStop, onResume, onRetryFailed, onRename, anotherRunning, showToast }) => {
    const stats = useMemo(() => summarize(recs), [recs]);
    const processed = recs.length;
    const pct = scan.total ? Math.floor((processed / scan.total) * 100) : 0;
    const incomplete = processed < scan.total;
    const [editingName, setEditingName] = useState(false);
    const [nameDraft, setNameDraft] = useState(scan.name);
    useEffect(() => setNameDraft(scan.name), [scan.name]);

    return (
        <div className="bg-white rounded-xl shadow-lg p-6 relative">
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-600 hover:text-gray-800 p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>

            <div className="mb-5 pr-10">
                {editingName ? (
                    <div className="flex items-center gap-2">
                        <input autoFocus value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { onRename(nameDraft); setEditingName(false); } if (e.key === 'Escape') setEditingName(false); }}
                            className="text-2xl font-bold text-gray-800 border-b-2 border-teal-500 outline-none flex-1" />
                        <button onClick={() => { onRename(nameDraft); setEditingName(false); }} className="px-3 py-1 rounded-lg bg-teal-600 text-white text-sm font-semibold">Save</button>
                    </div>
                ) : (
                    <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                        {scan.name}
                        <button onClick={() => setEditingName(true)} className="text-gray-400 hover:text-gray-700"><Edit3 size={18} /></button>
                    </h2>
                )}
                <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-gray-600">
                    {statusBadge(runInfo ? (runInfo.paused ? 'paused' : 'running') : scan.status)}
                    <span>{fmtNum(processed)} / {fmtNum(scan.total)} scanned ({pct}%)</span>
                    {scan.finishedAt && <span>Finished {new Date(scan.finishedAt).toLocaleString()}</span>}
                </div>
            </div>

            {/* Controls */}
            <div className="flex flex-wrap gap-2 mb-5">
                {runInfo && !runInfo.paused && <button onClick={onPause} className="px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold flex items-center gap-2"><Pause size={16} /> Pause</button>}
                {runInfo && runInfo.paused && <button onClick={onUnpause} className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold flex items-center gap-2"><Play size={16} /> Resume</button>}
                {runInfo && <button onClick={onStop} className="px-4 py-2 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm font-semibold flex items-center gap-2"><Square size={16} /> Stop</button>}
                {!runInfo && incomplete && (
                    <button onClick={onResume} disabled={anotherRunning}
                        className="px-4 py-2 rounded-lg bg-gradient-to-r from-teal-500 to-blue-600 text-white text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                        <Play size={16} /> Resume Scan ({fmtNum(scan.total - processed)} left)
                    </button>
                )}
                {!runInfo && stats.failedCount > 0 && (
                    <button onClick={onRetryFailed} disabled={anotherRunning}
                        className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
                        <RefreshCw size={16} /> Retry {fmtNum(stats.failedCount)} Failed
                    </button>
                )}
                {anotherRunning && <span className="text-xs text-gray-500 self-center">Another scan is running.</span>}
            </div>

            {runInfo && (
                <div className="mb-5 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
                    <Monitor size={16} /> Results appear live below. Keep this tab open until the scan finishes.
                </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
                <StatCard icon={<Package size={22} />} label="Scanned" value={fmtNum(stats.doneCount)} cls="bg-blue-50 border-blue-200 text-blue-800" />
                <StatCard icon={<CheckCircle size={22} />} label="With CC" value={fmtNum(stats.withCC)} cls="bg-green-50 border-green-200 text-green-800" />
                <StatCard icon={<XCircle size={22} />} label="No CC" value={fmtNum(stats.doneCount - stats.withCC)} cls="bg-gray-50 border-gray-200 text-gray-700" />
                <StatCard icon={<TrendingUp size={22} />} label="Campaigns" value={fmtNum(stats.campaignCount)} cls="bg-teal-50 border-teal-200 text-teal-800" />
                <StatCard icon={<DollarSign size={22} />} label="Avg / Top Comm" value={stats.campaignCount ? `${stats.avgComm.toFixed(1)}% / ${stats.topComm}%` : '-'} cls="bg-purple-50 border-purple-300 text-purple-800" />
                <StatCard icon={<AlertCircle size={22} />} label="Failed" value={fmtNum(stats.failedCount)} cls="bg-red-50 border-red-200 text-red-800" />
            </div>

            {loading ? (
                <div className="text-center py-12">
                    <RefreshCw className="animate-spin mx-auto text-teal-600 mb-4" size={48} />
                    <p className="text-gray-600 font-semibold">Loading results...</p>
                </div>
            ) : (
                <ResultsTable recs={recs} scanName={scan.name} showToast={showToast} />
            )}
        </div>
    );
};

const StatCard = ({ icon, label, value, sub, cls }) => (
    <div className={`border-2 rounded-lg p-3 ${cls}`}>
        <div className="flex items-center gap-2 opacity-80 text-xs font-semibold">{icon} {label}</div>
        <div className="text-xl font-bold mt-1">{value} {sub && <span className="text-sm font-semibold opacity-70">{sub}</span>}</div>
    </div>
);

// ═══════════════════════════════════════════════════════════════════════════
// Results table
// ═══════════════════════════════════════════════════════════════════════════
const DEFAULT_FILTERS = {
    search: '', cc: 'all', minComm: '', minBudget: '', minDays: '', maxBsr: '', minSales: '',
    minPrice: '', maxPrice: '', minIncome: '', bestPer: 'none', hideExpired: true,
};

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const SORT_GETTERS = {
    brand: r => (r.brand || '').toLowerCase(),
    title: r => (r.title || '').toLowerCase(),
    comm: r => r.comm,
    budget: r => r.budget,
    daysLeft: r => r.daysLeft,
    upper: r => r.inf?.upperVid ?? null,
    bottom: r => r.inf?.bottomVid ?? null,
    totalInf: r => r.inf?.totalInf ?? null,
    bsr: r => r.bsr,
    monthlySold: r => r.monthlySold,
    price: r => r.price,
    income: r => r.income,
};

const ResultsTable = ({ recs, scanName, showToast }) => {
    const [f, setF] = useState(DEFAULT_FILTERS);
    const [showFilters, setShowFilters] = useState(true);
    const [sort, setSort] = useState({ key: 'comm', dir: 'desc' });
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [selected, setSelected] = useState(() => new Set());

    const setFilter = (k, v) => setF(prev => ({ ...prev, [k]: v }));
    useEffect(() => { setPage(1); }, [f, sort, pageSize]);

    const rows = useMemo(() => flattenRows(recs, { hideExpired: f.hideExpired }), [recs, f.hideExpired]);

    const filtered = useMemo(() => {
        const q = f.search.trim().toLowerCase();
        const minComm = numOrNull(f.minComm), minBudget = numOrNull(f.minBudget), minDays = numOrNull(f.minDays);
        const maxBsr = numOrNull(f.maxBsr), minSales = numOrNull(f.minSales), minPrice = numOrNull(f.minPrice);
        const maxPrice = numOrNull(f.maxPrice), minIncome = numOrNull(f.minIncome);

        let out = rows.filter(r => {
            if (f.cc === 'cc' && r.noCC) return false;
            if (f.cc === 'nocc' && !r.noCC) return false;
            if (q && !`${r.asin} ${r.title} ${r.brand} ${r.campaignName} ${r.category}`.toLowerCase().includes(q)) return false;
            if (minComm !== null && (r.comm ?? -1) < minComm) return false;
            if (minBudget !== null && (r.budget ?? -1) < minBudget) return false;
            if (minDays !== null && (r.daysLeft === null || r.daysLeft < minDays)) return false;
            if (maxBsr !== null && (r.bsr === null || r.bsr === undefined || r.bsr > maxBsr)) return false;
            if (minSales !== null && (r.monthlySold === null || r.monthlySold === undefined || r.monthlySold < minSales)) return false;
            if (minPrice !== null && (r.price === null || r.price === undefined || r.price < minPrice)) return false;
            if (maxPrice !== null && (r.price === null || r.price === undefined || r.price > maxPrice)) return false;
            if (minIncome !== null && (r.income === null || r.income < minIncome)) return false;
            return true;
        });

        if (f.bestPer !== 'none') {
            const best = new Map();
            out.forEach(r => {
                const key = f.bestPer === 'asin' ? r.asin : (r.brand || '').toLowerCase();
                if (!key) return;
                const cur = best.get(key);
                const better = !cur
                    || (r.comm ?? -1) > (cur.comm ?? -1)
                    || ((r.comm ?? -1) === (cur.comm ?? -1) && (r.daysLeft ?? -1) > (cur.daysLeft ?? -1));
                if (better) best.set(key, r);
            });
            out = [...best.values()];
        }

        const get = SORT_GETTERS[sort.key] || SORT_GETTERS.comm;
        const dir = sort.dir === 'asc' ? 1 : -1;
        out = [...out].sort((a, b) => {
            const av = get(a), bv = get(b);
            const aNull = av === null || av === undefined || av === '';
            const bNull = bv === null || bv === undefined || bv === '';
            if (aNull && bNull) return 0;
            if (aNull) return 1;   // empty values always last
            if (bNull) return -1;
            return av < bv ? -dir : av > bv ? dir : 0;
        });
        return out;
    }, [rows, f, sort]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, totalPages);
    const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

    const activeFilterCount = Object.entries(f).filter(([k, v]) => v !== DEFAULT_FILTERS[k]).length;
    const allFilteredSelected = filtered.length > 0 && filtered.every(r => selected.has(r.key));
    const selectedRows = filtered.filter(r => selected.has(r.key));

    const toggleSort = (key) => setSort(s => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'bsr' || key === 'brand' || key === 'title' ? 'asc' : 'desc' });
    const toggleRow = (key) => setSelected(prev => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
    const toggleAll = () => setSelected(prev => {
        if (allFilteredSelected) { const n = new Set(prev); filtered.forEach(r => n.delete(r.key)); return n; }
        const n = new Set(prev); filtered.forEach(r => n.add(r.key)); return n;
    });

    const copyText = async (text, msg) => {
        try { await navigator.clipboard.writeText(text); showToast(msg); } catch { showToast('Could not copy to clipboard.', 'error'); }
    };

    const SortTh = ({ k, children, align = 'center' }) => (
        <th onClick={() => toggleSort(k)} className={`px-3 py-3 font-semibold cursor-pointer select-none whitespace-nowrap hover:bg-teal-700 transition text-${align}`}>
            <span className={`inline-flex items-center gap-1 ${align === 'center' ? 'justify-center' : ''}`}>
                {children}
                {sort.key === k ? (sort.dir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />) : <ArrowUpDown size={12} className="opacity-50" />}
            </span>
        </th>
    );

    const inputCls = 'w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent';

    return (
        <div>
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input value={f.search} onChange={(e) => setFilter('search', e.target.value)} placeholder="Search ASIN, title, brand, campaign..."
                        className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                </div>
                <div className="inline-flex bg-gray-100 rounded-lg p-1">
                    {[['all', 'All'], ['cc', 'Has CC'], ['nocc', 'No CC']].map(([v, l]) => (
                        <button key={v} onClick={() => setFilter('cc', v)} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${f.cc === v ? 'bg-white shadow text-teal-700' : 'text-gray-600'}`}>{l}</button>
                    ))}
                </div>
                <button onClick={() => setShowFilters(s => !s)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 border-2 transition ${activeFilterCount ? 'border-teal-300 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
                    <Filter size={16} /> Filters{activeFilterCount ? ` (${activeFilterCount})` : ''}
                </button>
                <button onClick={() => exportRows(filtered, scanName, 'xlsx')} disabled={!filtered.length}
                    className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 bg-green-50 text-green-700 border-2 border-green-200 hover:bg-green-100 disabled:opacity-50">
                    <Download size={16} /> Excel
                </button>
                <button onClick={() => exportRows(filtered, scanName, 'csv')} disabled={!filtered.length}
                    className="px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 bg-gray-50 text-gray-700 border-2 border-gray-200 hover:bg-gray-100 disabled:opacity-50">
                    <Download size={16} /> CSV
                </button>
            </div>

            {showFilters && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <label className="text-xs font-semibold text-gray-600">Commission % ≥<input type="number" value={f.minComm} onChange={(e) => setFilter('minComm', e.target.value)} placeholder="Any" className={inputCls} /></label>
                        <label className="text-xs font-semibold text-gray-600">Days left ≥<input type="number" value={f.minDays} onChange={(e) => setFilter('minDays', e.target.value)} placeholder="Any" className={inputCls} /></label>
                        <label className="text-xs font-semibold text-gray-600">BSR ≤<input type="number" value={f.maxBsr} onChange={(e) => setFilter('maxBsr', e.target.value)} placeholder="Any" className={inputCls} /></label>
                        <label className="text-xs font-semibold text-gray-600">Sales/mo ≥<input type="number" value={f.minSales} onChange={(e) => setFilter('minSales', e.target.value)} placeholder="Any" className={inputCls} /></label>
                        <label className="text-xs font-semibold text-gray-600">Price $ ≥<input type="number" value={f.minPrice} onChange={(e) => setFilter('minPrice', e.target.value)} placeholder="Any" className={inputCls} /></label>
                        <label className="text-xs font-semibold text-gray-600">Price $ ≤<input type="number" value={f.maxPrice} onChange={(e) => setFilter('maxPrice', e.target.value)} placeholder="Any" className={inputCls} /></label>
                        <label className="text-xs font-semibold text-gray-600">Est. Mo. Income $ ≥<input type="number" value={f.minIncome} onChange={(e) => setFilter('minIncome', e.target.value)} placeholder="Any" className={inputCls} /></label>
                        <label className="text-xs font-semibold text-gray-600">Keep only
                            <select value={f.bestPer} onChange={(e) => setFilter('bestPer', e.target.value)} className={inputCls}>
                                <option value="none">All campaigns</option>
                                <option value="asin">Best campaign per ASIN</option>
                                <option value="brand">Best campaign per brand</option>
                            </select>
                        </label>
                        <div className="flex flex-col justify-end gap-2">
                            <label className="flex items-center gap-2 text-sm text-gray-700 font-semibold cursor-pointer">
                                <input type="checkbox" checked={f.hideExpired} onChange={(e) => setFilter('hideExpired', e.target.checked)} className="w-4 h-4 accent-teal-600" />
                                Hide expired campaigns
                            </label>
                            <button onClick={() => setF(DEFAULT_FILTERS)} className="text-sm text-red-600 font-semibold text-left hover:underline">Clear all filters</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Selection bar */}
            {selected.size > 0 && (
                <div className="flex flex-wrap items-center gap-3 mb-3 bg-teal-50 border border-teal-200 rounded-lg px-4 py-2">
                    <span className="text-sm font-bold text-teal-800">{fmtNum(selectedRows.length)} selected</span>
                    <button onClick={() => copyText([...new Set(selectedRows.map(r => r.asin))].join('\n'), 'ASINs copied!')} className="text-sm font-semibold text-teal-700 flex items-center gap-1 hover:underline"><Copy size={14} /> Copy ASINs</button>
                    <button onClick={() => exportRows(selectedRows, `${scanName}_selected`, 'xlsx')} className="text-sm font-semibold text-teal-700 flex items-center gap-1 hover:underline"><Download size={14} /> Export selected</button>
                    <button onClick={() => setSelected(new Set())} className="text-sm font-semibold text-gray-600 hover:underline ml-auto">Clear selection</button>
                </div>
            )}

            <div className="text-sm text-gray-600 mb-2">
                Showing <b>{fmtNum(filtered.length)}</b> of {fmtNum(rows.length)} rows
            </div>

            {!rows.length ? (
                <div className="text-center py-12 text-gray-500">
                    <Package className="mx-auto text-gray-300 mb-3" size={56} />
                    {recs.length ? 'No successful results yet.' : 'Results will appear here as ASINs are scanned.'}
                </div>
            ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full text-sm">
                        <thead className="bg-gradient-to-r from-teal-600 to-blue-600 text-white sticky top-0 z-10">
                            <tr>
                                <th className="px-3 py-3 w-10 text-center">
                                    <input type="checkbox" checked={allFilteredSelected} onChange={toggleAll} className="w-4 h-4 accent-white cursor-pointer" title="Select all filtered rows" />
                                </th>
                                <th className="px-3 py-3 text-left font-semibold">Image</th>
                                <SortTh k="title" align="left">Product</SortTh>
                                <SortTh k="comm">Comm</SortTh>
                                <SortTh k="daysLeft">Days Left</SortTh>
                                <SortTh k="upper">Upper</SortTh>
                                <SortTh k="bottom">Bottom</SortTh>
                                <SortTh k="totalInf">Inf / Len</SortTh>
                                <SortTh k="bsr">BSR</SortTh>
                                <SortTh k="monthlySold">Sales/Mo</SortTh>
                                <SortTh k="price">Price</SortTh>
                                <SortTh k="income">Est. Mo. Income</SortTh>
                                <th className="px-3 py-3 text-center font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageRows.map((r, i) => (
                                <tr key={r.key} className={`border-b border-gray-100 hover:bg-teal-50/40 transition ${selected.has(r.key) ? 'bg-teal-50' : i % 2 ? 'bg-gray-50/60' : 'bg-white'}`}>
                                    <td className="px-3 py-2 text-center">
                                        <input type="checkbox" checked={selected.has(r.key)} onChange={() => toggleRow(r.key)} className="w-4 h-4 accent-teal-600 cursor-pointer" />
                                    </td>
                                    <td className="px-3 py-2">
                                        <a href={`https://www.amazon.com/dp/${r.asin}`} target="_blank" rel="noopener noreferrer">
                                            {r.image
                                                ? <img src={r.image} alt="" loading="lazy" className="w-12 h-12 object-contain rounded border border-gray-100 bg-white" />
                                                : <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center"><Package className="text-gray-400" size={20} /></div>}
                                        </a>
                                    </td>
                                    <td className="px-3 py-2 max-w-[280px]">
                                        <div className="text-xs font-bold text-blue-700 uppercase truncate">{r.brand || '-'}</div>
                                        <div className="font-semibold text-gray-800 line-clamp-2" title={r.title}>{r.title || r.campaignName || '-'}</div>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="font-mono text-xs text-gray-500">{r.asin}</span>
                                            {r.noCC && <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">NO CC</span>}
                                        </div>
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        {r.noCC ? <span className="text-gray-400">-</span> : (
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${r.comm >= 20 ? 'bg-green-600 text-white' : r.comm >= 10 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}`}>{r.comm}%</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-center">
                                        {r.daysLeft === null ? <span className="text-gray-400">-</span> : (
                                            <span className={`font-semibold ${r.daysLeft <= 3 ? 'text-red-600' : r.daysLeft <= 7 ? 'text-amber-600' : 'text-gray-700'}`}>{r.daysLeft}d</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-center text-xs">
                                        {r.inf ? <span className="bg-amber-50 text-amber-800 px-2 py-1 rounded">{carouselStr(r.inf.upperVid, r.inf.upperInf)}</span> : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-center text-xs">
                                        {r.inf ? <span className="bg-pink-50 text-pink-800 px-2 py-1 rounded">{carouselStr(r.inf.bottomVid, r.inf.bottomInf)}</span> : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-center text-xs" title={r.inf?.isKeepa ? 'Average video length (seconds)' : 'Total influencer videos'}>
                                        {r.inf?.totalInf !== null && r.inf?.totalInf !== undefined
                                            ? <span className="bg-purple-50 text-purple-800 px-2 py-1 rounded">{r.inf.totalInf}{r.inf.isKeepa ? 's' : '+'}</span>
                                            : '-'}
                                    </td>
                                    <td className="px-3 py-2 text-center text-gray-700">{r.bsr ? `#${fmtNum(r.bsr)}` : '-'}</td>
                                    <td className="px-3 py-2 text-center text-gray-700">{r.monthlySold !== null && r.monthlySold !== undefined ? `${fmtNum(r.monthlySold)}+` : (r.bsrDrops ? `${r.bsrDrops} drops` : '-')}</td>
                                    <td className="px-3 py-2 text-center text-gray-700">{fmtMoney(r.price, 2)}</td>
                                    <td className="px-3 py-2 text-center font-semibold text-teal-700">{r.income !== null && r.income !== undefined ? fmtMoney(r.income, 2) : '-'}</td>
                                    <td className="px-3 py-2">
                                        <div className="flex items-center justify-center gap-1">
                                            {r.campaignId ? (
                                                <a href={ccCampaignUrl(r.campaignId)} target="_blank" rel="noopener noreferrer"
                                                    className="px-2.5 py-1.5 rounded-md bg-gradient-to-r from-teal-500 to-blue-600 text-white text-xs font-semibold whitespace-nowrap hover:shadow">Open CC</a>
                                            ) : (
                                                <a href={ccSearchUrl(r.asin)} target="_blank" rel="noopener noreferrer"
                                                    className="px-2.5 py-1.5 rounded-md bg-gray-100 text-gray-700 text-xs font-semibold whitespace-nowrap hover:bg-gray-200">Find CC</a>
                                            )}
                                            <a href={`https://www.amazon.com/dp/${r.asin}`} target="_blank" rel="noopener noreferrer" title="Open on Amazon"
                                                className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"><ExternalLink size={14} /></a>
                                            <button onClick={() => copyText(r.asin, `${r.asin} copied!`)} title="Copy ASIN"
                                                className="p-1.5 rounded-md text-gray-600 hover:bg-gray-100"><Copy size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Pagination */}
            {filtered.length > 0 && (
                <div className="flex flex-wrap items-center justify-center gap-4 mt-4">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1">
                        <ChevronLeft size={16} /> Previous
                    </button>
                    <span className="text-sm text-gray-800">Page {safePage} of {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1">
                        Next <ChevronRight size={16} />
                    </button>
                    <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                        className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white">
                        {PAGE_SIZES.map(s => <option key={s} value={s}>{s} / page</option>)}
                    </select>
                </div>
            )}

            <p className="text-xs text-gray-400 text-center mt-4">
                Est. Mo. Income is a rough estimate based on monthly sales, price, commission and video competition.
            </p>
        </div>
    );
};

export default AipCsvScanPage;